/**
 * PostRunUnlockScreen.tsx — TheBurntPeanut Simulator 9000
 *
 * Shown after a run ends if new characters or percs were unlocked.
 * Renders over a dark overlay in the chalk/game aesthetic before
 * returning the player to the main menu.
 */

import { PEANUT_PERCS } from '../game/peanutPercs';
import type { GoopSystemState } from '../game/goopSystem';

// ── Quest → reward label mapping ──────────────────────────────────────────────
const CHAR_QUEST_REWARDS: Record<string, { label: string; sub: string }> = {
  q1: { label: 'KILLA PEANUT UNLOCKED',    sub: 'RPK Machine Gun — Suppression Fire' },
  q2: { label: 'WIZARD PEANUT UNLOCKED',   sub: 'Fireball Wand — Arcane Surge' },
  q3: { label: 'VIKING PEANUT UNLOCKED',   sub: 'Viking Axe — Berserker Rage' },
  q4: { label: 'MR. SILLY PEANUT UNLOCKED', sub: 'VooGoo Rager Toss — Silly Juice' },
  q5: { label: 'LEBRON PEANUT UNLOCKED',   sub: 'Tactical Basketball — King\'s Court' },
  q6: { label: 'CHAD PEANUT UNLOCKED',     sub: 'The Chad Aura — Golden Aura' },
};

interface UnlockEntry {
  label: string;
  sub: string;
  isChar: boolean;
}

function resolveUnlocks(questIds: string[], goopState: GoopSystemState): UnlockEntry[] {
  const entries: UnlockEntry[] = [];
  for (const qid of questIds) {
    // Character quest
    if (CHAR_QUEST_REWARDS[qid]) {
      entries.push({ ...CHAR_QUEST_REWARDS[qid], isChar: true });
      continue;
    }
    // Perc quest (prefixed with "perc_")
    if (qid.startsWith('perc_')) {
      const percId = qid.slice(5);
      const perc = PEANUT_PERCS.find(p => p.id === percId);
      if (perc) {
        entries.push({
          label: `${perc.name.toUpperCase()} UNLOCKED`,
          sub: perc.description,
          isChar: false,
        });
      }
    }
  }
  return entries;
}

// ── Component ─────────────────────────────────────────────────────────────────
interface Props {
  questIds: string[];
  goopState: GoopSystemState;
  onContinue: () => void;
}

const C = {
  bg:        'rgba(0,0,0,0.88)',
  panel:     'rgba(12,18,12,0.97)',
  border:    'rgba(255,204,0,0.55)',
  gold:      '#ffd700',
  amber:     '#ffcc00',
  green:     '#39ff14',
  white:     '#f0ece0',
  dim:       'rgba(240,236,224,0.5)',
  charBg:    'rgba(255,204,0,0.08)',
  percBg:    'rgba(57,255,20,0.07)',
};

export default function PostRunUnlockScreen({ questIds, goopState, onContinue }: Props) {
  const unlocks = resolveUnlocks(questIds, goopState);

  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: C.bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999,
    fontFamily: "'Share Tech Mono', monospace",
  };

  const cardStyle: React.CSSProperties = {
    background: C.panel,
    border: `2px solid ${C.border}`,
    borderRadius: 12,
    padding: '40px 48px',
    minWidth: 480,
    maxWidth: 640,
    boxShadow: `0 0 40px rgba(255,204,0,0.2), 0 0 80px rgba(0,0,0,0.8)`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: "'Orbitron', monospace",
    fontSize: '1.5rem',
    fontWeight: 700,
    color: C.gold,
    letterSpacing: '0.2em',
    textShadow: `0 0 20px rgba(255,215,0,0.7)`,
    textAlign: 'center',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '0.78rem',
    color: C.dim,
    letterSpacing: '0.15em',
    marginTop: -16,
    textAlign: 'center',
  };

  const listStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  };

  const continueStyle: React.CSSProperties = {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '0.9rem',
    letterSpacing: '0.2em',
    color: '#000',
    background: C.gold,
    border: 'none',
    borderRadius: 6,
    padding: '12px 36px',
    cursor: 'pointer',
    fontWeight: 700,
    marginTop: 8,
    boxShadow: `0 0 20px rgba(255,215,0,0.5)`,
    transition: 'transform 0.1s, box-shadow 0.1s',
  };

  return (
    <div style={panelStyle}>
      <div style={cardStyle}>
        <div style={titleStyle}>🔓 RUN COMPLETE</div>
        <div style={subtitleStyle}>NEW UNLOCKS THIS RUN</div>

        <div style={listStyle}>
          {unlocks.length === 0 ? (
            <div style={{ color: C.dim, textAlign: 'center', fontSize: '0.85rem' }}>
              No new unlocks — keep grinding!
            </div>
          ) : (
            unlocks.map((u, i) => (
              <div
                key={i}
                style={{
                  background: u.isChar ? C.charBg : C.percBg,
                  border: `1px solid ${u.isChar ? 'rgba(255,204,0,0.35)' : 'rgba(57,255,20,0.25)'}`,
                  borderRadius: 8,
                  padding: '14px 20px',
                }}
              >
                <div style={{
                  color: u.isChar ? C.amber : C.green,
                  fontWeight: 700,
                  fontSize: '0.88rem',
                  letterSpacing: '0.12em',
                  marginBottom: 4,
                }}>
                  ✦ {u.label}
                </div>
                <div style={{
                  color: C.dim,
                  fontSize: '0.75rem',
                  letterSpacing: '0.08em',
                }}>
                  {u.sub}
                </div>
              </div>
            ))
          )}
        </div>

        <button
          style={continueStyle}
          onClick={onContinue}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 32px rgba(255,215,0,0.8)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 20px rgba(255,215,0,0.5)';
          }}
        >
          CONTINUE →
        </button>
      </div>
    </div>
  );
}
